import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Form, Input, Button, Select, DatePicker, InputNumber, Row, Col, message, Typography, Spin } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import dayjs from "dayjs";
import { API_CONST } from "../../../const";
import useLocationOptions from "../../../hooks/useLocationOptions";

const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const CreateEngagementWrapper = styled.div`
  padding: 24px;

  .header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;

    .back-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 8px;
      border: 1px solid #d9d9d9;
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        border-color: #01d9a9;
        color: #01d9a9;
      }
    }

    h2 {
      margin: 0;
      font-size: 24px;
      color: #014c75;
    }
  }

  .form-card {
    border-radius: 12px;
    max-width: 900px;

    .ant-card-body {
      padding: 32px;
    }
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #014c75;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
  }

  .form-section {
    margin-bottom: 32px;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #f0f0f0;
  }

  .info-text {
    color: #8c8c8c;
    font-size: 13px;
    margin-top: 4px;
  }
`;

const engagementTypes = [
  "Fixed Price Project",
  "Time & Material",
  "Dedicated Team",
  "Staff Augmentation",
  "Managed Services",
  "Consulting",
];

const urgencyOptions = [
  { value: "low", label: "Low - Flexible timeline" },
  { value: "medium", label: "Medium - Standard timeline" },
  { value: "high", label: "High - Need to start soon" },
  { value: "critical", label: "Critical - Immediate need" },
];

const durationUnits = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" },
];

const AdminCreateEngagement = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [customerOrganizations, setCustomerOrganizations] = useState([]);
  const isEditMode = !!id;
  const { countries, cities, fetchCities } = useLocationOptions();

  useEffect(() => {
    fetchCustomerOrganizations();
    if (isEditMode) {
      fetchEngagementData();
    }
  }, [id]);

  const fetchCustomerOrganizations = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.GET_CUSTOMER_ORGANIZATIONS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomerOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      message.error("Failed to load customer organizations");
    }
  };

  const fetchEngagementData = async () => {
    setFetchingData(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.GET_ENGAGEMENT}/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const engagement = response.data.engagement;
      
      form.setFieldsValue({
        customerOrganization: engagement.customerOrganization,
        title: engagement.title,
        description: engagement.description,
        engagementType: engagement.engagementType,
        expectedStartDate: engagement.expectedStartDate ? dayjs(engagement.expectedStartDate) : null,
        expectedDuration: engagement.expectedDuration,
        durationUnit: engagement.durationUnit || "months",
        preferredLocations: engagement.preferredLocations || [],
        hiringUrgency: engagement.hiringUrgency,
        country: engagement.country || undefined,
      });
      if (engagement.country) {
        fetchCities(engagement.country);
      }
    } catch (error) {
      console.error("Error fetching engagement:", error);
      message.error("Failed to load engagement data");
      navigate("/admin/engagements");
    } finally {
      setFetchingData(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const payload = {
        customerOrganization: values.customerOrganization,
        title: values.title,
        description: values.description,
        engagementType: values.engagementType,
        expectedStartDate: values.expectedStartDate?.format("YYYY-MM-DD"),
        expectedDuration: values.expectedDuration,
        durationUnit: values.durationUnit || "months",
        preferredLocations: values.preferredLocations || [],
        hiringUrgency: values.hiringUrgency,
        country: values.country || undefined,
      };

      if (isEditMode) {
        await axios.put(`${API_CONST.UPDATE_ENGAGEMENT}/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Engagement updated successfully!");
      } else {
        await axios.post(API_CONST.CREATE_ENGAGEMENT, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Engagement created successfully!");
      }
      navigate("/admin/engagements");
    } catch (error) {
      console.error("Error saving engagement:", error);
      message.error(error.response?.data?.message || `Failed to ${isEditMode ? "update" : "create"} engagement`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CreateEngagementWrapper>
      <div className="header">
        <div className="back-btn" onClick={() => navigate("/admin/engagements")}>
          <ArrowLeftOutlined />
        </div>
        <h2>{isEditMode ? "Edit Engagement" : "Create Engagement on Behalf of Customer"}</h2>
      </div>

      <Spin spinning={fetchingData}>
      <Card className="form-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            durationUnit: "months",
            hiringUrgency: "medium",
          }}
        >
          <div className="form-section">
            <div className="section-title">Customer Information</div>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  label="Customer Organization"
                  name="customerOrganization"
                  rules={[{ required: true, message: "Please select a customer organization" }]}
                >
                  <Select
                    placeholder="Select customer organization"
                    showSearch
                    optionFilterProp="children"
                    disabled={isEditMode}
                  >
                    {customerOrganizations.map((org) => (
                      <Option key={org.id} value={org.name}>
                        {org.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                {!isEditMode && (
                  <p className="info-text">
                    Select the customer organization for which this engagement is being created.
                  </p>
                )}
              </Col>
            </Row>
          </div>

          <div className="form-section">
            <div className="section-title">Basic Information</div>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  label="Engagement Title"
                  name="title"
                  rules={[{ required: true, message: "Please enter engagement title" }]}
                >
                  <Input placeholder="e.g., Mobile App Development Project" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Short Description" name="description">
                  <TextArea
                    rows={3}
                    placeholder="Brief description of the engagement scope and objectives"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Engagement Type"
                  name="engagementType"
                  rules={[{ required: true, message: "Please select engagement type" }]}
                >
                  <Select placeholder="Select type">
                    {engagementTypes.map((type) => (
                      <Option key={type} value={type}>
                        {type}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Hiring Urgency" name="hiringUrgency">
                  <Select placeholder="Select urgency">
                    {urgencyOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="form-section">
            <div className="section-title">Timeline</div>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label="Expected Start Date" name="expectedStartDate">
                  <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Expected Duration" name="expectedDuration">
                  <InputNumber min={1} style={{ width: "100%" }} placeholder="Duration" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Unit" name="durationUnit">
                  <Select>
                    {durationUnits.map((unit) => (
                      <Option key={unit.value} value={unit.value}>
                        {unit.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="form-section">
            <div className="section-title">Location Preferences</div>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label="Country" name="country">
                  <Select
                    placeholder="Select country"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    onChange={(val) => {
                      fetchCities(val);
                      form.setFieldsValue({ preferredLocations: [] });
                    }}
                  >
                    {countries.map((c) => (
                      <Option key={c.value} value={c.value}>{c.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Preferred Locations" name="preferredLocations">
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Select preferred work locations"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    <Option value="Remote">Remote</Option>
                    <Option value="Hybrid">Hybrid</Option>
                    <Option value="On-site">On-site</Option>
                    {cities.map((c) => (
                      <Option key={c.value} value={c.value}>{c.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="form-actions">
            <Button onClick={() => navigate("/admin/engagements")}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
              {isEditMode ? "Update Engagement" : "Create Engagement"}
            </Button>
          </div>
        </Form>
      </Card>
      </Spin>

      <Paragraph style={{ marginTop: 16, color: "#8c8c8c", textAlign: "center" }}>
        Engagements created by admin are automatically approved and activated.
      </Paragraph>
    </CreateEngagementWrapper>
  );
};

export default AdminCreateEngagement;
